import React, { useEffect, useMemo } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Spinner,
  Button,
  Table,
  Checkbox,
  Label,
} from '@heroui/react';
import { useQuery } from '@tanstack/react-query';
import { getTopLevelDomain } from '../utils/get-top-level-domain';
import rrwebPlayer from 'rrweb-player';
import { Resizable } from 're-resizable';
import JsonView from '@uiw/react-json-view';
import ReactAceDefault from 'react-ace';
const AceEditor = ((ReactAceDefault as unknown as { default?: typeof ReactAceDefault })
  .default ?? ReactAceDefault) as typeof ReactAceDefault;
import 'ace-builds/src-noconflict/theme-textmate';
import logo from '../assets/logo-dark-colored.svg';
import 'rrweb-player/dist/style.css';
import { EventRecord } from '../utils/event-record';
import { EventLogSummary } from '../utils/event-log-summary';
import useLocalStorage from '../utils/use-local-storage';
import { IconArrowLeft, IconExternalLink, IconAlertTriangle } from '@tabler/icons-react';
import { NetworkTable } from '../components/NetworkTable';
import { DebuggerEvent } from '../utils/network-event-record';
import { formatState } from './index';

type PlayerSearch = {
  crawlrunGuid?: string;
  taskGuid?: string;
  key: string;
  apiKey?: string;
};

export const Route = createFileRoute('/player')({
  component: PlayerPage,
  validateSearch: (search: Record<string, unknown>): PlayerSearch => {
    // validate and parse the search params into a typed state
    return {
      crawlrunGuid: (search.crawlrunGuid as string) || '',
      taskGuid: (search.taskGuid as string) || '',
      key: search.key as string,
      apiKey: search._apikey as string,
    };
  },
  onLeave: () => {
    try {
      // @ts-expect-error Destroy is not in types
      window.player?.$destroy();
      window.player = undefined;
    } catch {
      // Do nothing
    }
  },
});

function PlayerPage() {
  const routeSearch = Route.useSearch();
  const navigate = useNavigate();

  const [tab, setTab] = React.useState('summary');
  const [playedEvents, setPlayedEvents] = React.useState<EventRecord[]>([]);
  const [debuggerBlob, setDebuggerBlob] = React.useState<string | null>(null);
  const [isScrollable, setScrollable] = useLocalStorage('scrollable', false);
  const [isShowLastData, setShowLastData] = useLocalStorage<boolean>('showLastData', false);
  const [sidebarWidth, setSidebarWidth] = useLocalStorage('sidebarWidth', 450);

  const apiKey = routeSearch?.apiKey ?? '';
  const sessionKey = routeSearch?.key ?? '';

  const fileNameParts = sessionKey.split('/');
  const fileName = fileNameParts[fileNameParts.length - 1];

  let host = 'import.io';
  if (sessionKey.includes('staging') || location.host.includes('staging')) {
    host = 'staging-owl.com';
  } else if (sessionKey.includes('demo') || location.host.includes('demo')) {
    host = 'demo-owl.com';
  }

  const debuggerFile = `https://api.${getTopLevelDomain()}/eventlog_new/download/${fileName}?key=${encodeURIComponent(sessionKey)}&${apiKey ? `_apikey=${apiKey}` : ''}`;
  const summaryFile = `https://api.${getTopLevelDomain()}/eventlog_new/summary/${fileName}?key=${encodeURIComponent(sessionKey)}&${apiKey ? `_apikey=${apiKey}` : ''}`;

  // Queries
  const query = useQuery<EventRecord[]>({
    queryKey: ['player', debuggerFile, fileName],
    refetchOnWindowFocus: false,
    refetchInterval: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    staleTime: Infinity,
    queryFn: async (): Promise<null | any> => {
      if (!fileName) {
        return null;
      }
      const data = await fetch(debuggerFile, {
        credentials: 'include',
      }).then((res) => res.json());

      if (data.code) {
        // if (data.code === 2004) {
        //   window.location.href = `https://enter.${getTopLevelDomain()}/?redirect=${encodeURIComponent(window.location.href)}`
        // }
        throw new Error(data.message);
      }

      console.log('Data loaded');

      setDebuggerBlob(
        URL.createObjectURL(
          new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }),
        ),
      );

      const engine = data.find((e: any) => e.type === 'firehose-record')?.event?.browser_engine;
      const isNightmare = engine && engine === 'nightmare';

      if (isNightmare) {
        // Redirect to old debugger
        window.location.replace(
          `https://debugger-old.${host}/player?${window.location.search.slice(1)}`,
        );
      }

      return data;
    },
  });

  // Summary query - fetches aggregated summary data from server
  const summaryQuery = useQuery<EventLogSummary>({
    queryKey: ['summary', summaryFile, fileName],
    refetchOnWindowFocus: false,
    refetchInterval: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    staleTime: Infinity,
    queryFn: async (): Promise<EventLogSummary> => {
      if (!fileName) {
        return {};
      }
      const data = await fetch(summaryFile, {
        credentials: 'include',
      }).then((res) => res.json());

      if (data.code) {
        throw new Error(data.message);
      }

      return data;
    },
  });

  useEffect(() => {
    if (query.data && !window.player) {
      const events = query
        .data!.filter((event: any) => event.type === 'tree-mirror')
        .map((event: any) => event.event);

      const lastEventTime = events[events.length - 1]?.timestamp;

      if (events.length >= 2) {
        window.player = new rrwebPlayer({
          target: document.getElementById('player-container')!,
          props: {
            events: events,
            autoPlay: true,
          },
        });

        window.player.getReplayer().on('event-cast', (event) => {
          if ((event as { timestamp: number }).timestamp === lastEventTime) {
            console.log('Last event played');
            setPlayedEvents(query.data);
            return;
          }
          setPlayedEvents(
            query.data.filter((e) => {
              return e.timestamp < (event as { timestamp: number }).timestamp;
            }),
          );
        });
      } else {
        setPlayedEvents(query.data);
      }
    }

    // Perform any setup or initialization here
    // For example, fetching data, subscribing to events, etc.

    return () => {
      // This code runs when the component is unmounted
      console.log('Component is unmounted');
    };
  }, [sessionKey, !!query.data]);

  const finalMessage = useMemo(() => {
    const allMessages = (query.data ?? []).filter(
      (event: any) => event.type === 'task-processing-message-sent',
    );
    return allMessages[allMessages.length - 1]?.event;
  }, [sessionKey, !!query.data]);

  const metricsMessage = useMemo(() => {
    return (query.data ?? []).find((event) => event.type === 'firehose-record')?.event;
  }, [sessionKey, !!query.data]);

  const firstConsoleEvent = useMemo(() => {
    return (query.data ?? []).find((e) => e.type === 'website-console-updated');
  }, [sessionKey, !!query.data]);

  const tracer = useMemo(() => {
    return query.data?.find((e) => e.type === 'tracer')?.event?.traceEvents;
  }, [sessionKey, !!query.data]);

  // Detect if user has advanced features (events other than tree-mirror)
  const hasAdvancedFeatures = useMemo(() => {
    if (!query.data) return false;
    return query.data.some((e) => e.type !== 'tree-mirror' && e.type !== 'tree-mirror-version');
  }, [sessionKey, !!query.data]);

  const consoleText = playedEvents
    .filter((e) => e.type === 'website-console-updated' && e?.event?.args?.length > 0)
    .map((e) => {
      const [logType, ...logArgs] = e.event.args ?? [];
      return `[${Math.round(e.timestamp - (firstConsoleEvent?.timestamp ?? 0))
        .toString()
        .padStart(
          6,
          ' ',
        )}ms] [${(logType?.toString() ?? '???').padStart(5, ' ')}] ${logArgs.join(' ')}`;
    })
    .join('\n');

  const dataItems = playedEvents
    .filter((e) => e.type === 'action-ended' && e?.event?.name === 'ExtractDataAction')
    .map((e) => {
      return e.event.result;
    });

  const lastDataItem = dataItems[dataItems.length - 1] ?? '';

  const playedActions = playedEvents.filter(
    (e) => e.type === 'action-started' || e.type === 'action-ended' || e.type === 'page-stage',
  );

  const gotoOptions = playedEvents.filter((e) => e.type === 'goto-options')?.[0]?.event || {};

  const debuggerEvents = playedEvents.filter((e) => e.type === 'debugger') as DebuggerEvent[];

  // Basic users only see Summary tab, advanced users see all tabs
  const tabs = hasAdvancedFeatures
    ? [
      [
        { title: 'None', key: 'none' },
          { title: 'Summary', key: 'summary' },
          { title: 'Final message', key: 'final' },
          { title: 'Metrics', key: 'metrics' },
          { title: 'Inputs', key: 'inputs' },
          { title: 'Data', key: 'data' },
        ],
        [
          { title: 'Options', key: 'goto' },
          { title: 'Console', key: 'console' },
          { title: 'Network', key: 'network' },
          { title: 'Actions', key: 'actions' },
          { title: 'Tracer', key: 'tracer' },
        ],
      ]
    : [[{ title: 'Summary', key: 'summary' }]];

  // Make iframe scrollable when checkbox is checked
  useEffect(() => {
    const iframe: HTMLIFrameElement | null = document.querySelector(
      '.replayer-wrapper iframe',
    ) as HTMLIFrameElement;

    if (iframe) {
      iframe.setAttribute('scrolling', isScrollable ? 'yes' : 'no');
      // enable pointer events on iframe
      iframe.style.pointerEvents = isScrollable ? 'auto' : 'none';
    }
  }, [isScrollable, window.player, query.data]);

  return (
    <div>
      <div className='h-screen flex flex-col'>
        <nav
          className='flex flex-row justify-between items-center bg-[var(--ink-0)]'
          style={{ borderBottom: '1px solid var(--ink-200)' }}
        >
          <div className='px-6 py-3 flex flex-row items-center gap-6'>
            <img
              src={logo}
              alt='Import.io'
              width={120}
            />
            <button
              className='btn-ghost inline-flex items-center gap-1.5'
              onClick={() =>
                void navigate({
                  to: '/',
                  search: {
                    crawlRunId: routeSearch.crawlrunGuid!,
                    ...(routeSearch.apiKey ? { _apikey: routeSearch.apiKey } : {}),
                  },
                })
              }
            >
              <IconArrowLeft size={16} />
              Back
            </button>
            {finalMessage?.runId && (
              <div className='flex flex-col gap-1 text-[12px] leading-tight'>
                <div className='inline-flex items-baseline gap-2'>
                  <span className='eyebrow w-20'>Crawlrun</span>
                  <span className='mono text-[var(--ink-900)]'>
                    {finalMessage.runId}
                    {hasAdvancedFeatures && debuggerBlob && (
                      <>
                        {' '}
                        <a
                          className='text-[var(--accent)] hover:underline'
                          href={debuggerBlob}
                          download={routeSearch.crawlrunGuid + '.json'}
                        >
                          json
                        </a>
                      </>
                    )}
                  </span>
                </div>
                <div className='inline-flex items-baseline gap-2'>
                  <span className='eyebrow w-20'>Task</span>
                  <span className='mono text-[var(--ink-900)]'>{finalMessage.taskId}</span>
                </div>
                <div className='inline-flex items-baseline gap-2'>
                  <span className='eyebrow w-20'>Extractor</span>
                  <a
                    className='mono inline-flex items-center text-[var(--accent)] hover:underline'
                    target='_blank'
                    href={`https://app.${host}/${metricsMessage.extractor_id}/`}
                    rel='noreferrer'
                  >
                    {metricsMessage.extractor_id}
                    <IconExternalLink size={12} className='ml-1' />
                  </a>
                </div>
                <div className='inline-flex items-center gap-2'>
                  <span className='eyebrow w-20'>Scrollable</span>
                  <Checkbox
                    isSelected={isScrollable}
                    onChange={setScrollable}
                  >
                    <Checkbox.Control>
                      <Checkbox.Indicator />
                    </Checkbox.Control>
                  </Checkbox>
                </div>
              </div>
            )}
          </div>
          <div className='flex justify-center items-center px-4 py-3'>
            <div className='flex flex-col gap-1.5 items-end'>
              {tabs.map((tabList, tabIndex) => {
                return (
                  <div className='tabbar' key={'tabs-' + tabIndex}>
                    {tabList.map((t) => {
                      return (
                        <Button
                          key={t.key}
                          size='sm'
                          variant='secondary'
                          data-active={tab === t.key ? '' : undefined}
                          onPress={() => setTab(t.key)}
                        >
                          {t.title}
                        </Button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </nav>
        <div className='flex flex-row flex-1 overflow-hidden'>
          <div
            className='flex-1 flex items-center justify-center overflow-auto'
            style={{ backgroundColor: '#b5bcbf' }}
          >
            {query.isLoading ? <Spinner /> : null}
            {query.isError ? <div>{query.error.message}</div> : null}
            {query.data ? <div id='player-container'></div> : null}
          </div>
          <Resizable
            defaultSize={{
              width: sidebarWidth,
            }}
            minWidth={150}
            maxWidth={1000}
            onResizeStop={(_e, _direction, _ref, d) => {
              setSidebarWidth(sidebarWidth + d.width);
            }}
            className='bg-[var(--ink-50)] border-l border-[var(--ink-200)]'
            style={{
              display: tab === 'none' ? 'none' : 'block',
            }}
          >
            {query.data && (
              <div className='h-full overflow-y-auto'>
                {tab === 'summary' && (
                  <div className='overflow-y-auto p-4'>
                    {summaryQuery.isLoading && <Spinner />}
                    {summaryQuery.isError && (
                      <div className='flex flex-col items-center justify-center p-6'>
                        <div className='bg-red-50 border border-red-200 rounded-xl p-6 max-w-md w-full shadow-sm'>
                          <div className='flex items-center gap-3 mb-4'>
                            <div className='bg-red-100 rounded-full p-2'>
                              <IconAlertTriangle className='text-red-600' size={24} />
                            </div>
                            <h3 className='text-lg font-semibold text-red-800'>
                              Failed to Load Summary
                            </h3>
                          </div>
                          <p className='text-red-700 text-sm mb-4'>
                            {summaryQuery.error.message}
                          </p>
                          <div className='bg-red-100/50 rounded-lg p-3 text-xs text-red-600'>
                            <p className='font-medium mb-1'>Details:</p>
                            <p className='font-mono break-all'>
                              {summaryFile.split('?')[0].split('/').pop()}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    {summaryQuery.data && (() => {
                      const data = summaryQuery.data;
                      const rows: { key: string; label: string; value: React.ReactNode }[] = [];

                      if (data.status != null) {
                        rows.push({ key: 'status', label: 'Status', value: formatState(data.status) });
                      }
                      if (data.errorMessage != null) {
                        rows.push({ key: 'error', label: 'Error', value: <strong>{data.errorMessage}</strong> });
                      }
                      if (data.finalUrl != null) {
                        rows.push({ key: 'finalUrl', label: 'Final url', value: data.finalUrl });
                      }
                      if (data.statusCode != null) {
                        rows.push({ key: 'statusCode', label: 'Status code', value: data.statusCode });
                      }
                      if (data.rowsExtracted != null) {
                        rows.push({ key: 'rowsExtracted', label: 'Rows extracted', value: data.rowsExtracted });
                      }
                      if (data.cell != null) {
                        rows.push({ key: 'cell', label: 'Cell', value: data.cell });
                      }
                      if (data.browser != null) {
                        rows.push({ key: 'browser', label: 'Browser', value: data.browser });
                      }
                      if (data.browserEngine != null) {
                        rows.push({ key: 'engine', label: 'Engine', value: data.browserEngine });
                      }
                      if (data.engineVersion != null) {
                        rows.push({ key: 'engineVersion', label: 'Engine version', value: data.engineVersion });
                      }
                      if (metricsMessage?.collection_slug) {
                        const wbBase = 'https://workbench.import.io';
                        const orgUrl = `${wbBase}/orgs/${metricsMessage.org_slug}`;
                        const projectUrl = `${orgUrl}/projects/${metricsMessage.project_slug}`;
                        const collectionUrl = `${projectUrl}/collections/${metricsMessage.collection_slug}`;
                        const sourceUrl = `${collectionUrl}/sources/${metricsMessage.source_slug}/home`;
                        const linkClass = 'text-[var(--accent)] hover:underline';
                        rows.push({
                          key: 'workbench',
                          label: 'Workbench',
                          value: (
                            <span>
                              <a className={linkClass} target='_blank' rel='noreferrer' href={orgUrl}>
                                {metricsMessage.org_slug}
                              </a>
                              {' / '}
                              <a className={linkClass} target='_blank' rel='noreferrer' href={projectUrl}>
                                {metricsMessage.project_slug}
                              </a>
                              {' / '}
                              <a className={linkClass} target='_blank' rel='noreferrer' href={sourceUrl}>
                                {metricsMessage.source_slug}
                              </a>
                              {' / '}
                              <a className={linkClass} target='_blank' rel='noreferrer' href={collectionUrl}>
                                {metricsMessage.collection_slug}
                              </a>
                            </span>
                          ),
                        });
                        if (metricsMessage.snapshot_id) {
                          const snapshotUrl = `${collectionUrl}/sources/${metricsMessage.source_slug}/snapshots/${metricsMessage.snapshot_id}/home`;
                          rows.push({
                            key: 'snapshot',
                            label: 'Snapshot',
                            value: (
                              <a
                                className={`${linkClass} inline-flex items-center`}
                                target='_blank'
                                rel='noreferrer'
                                href={snapshotUrl}
                              >
                                {metricsMessage.snapshot_id}
                                <IconExternalLink size={12} className='ml-1' />
                              </a>
                            ),
                          });
                        }
                      }
                      if (data.retryNum != null || data.retryCount != null) {
                        rows.push({ key: 'retry', label: 'Retry', value: `${data.retryNum} / ${data.retryCount}` });
                      }
                      if (data.proxyType != null || data.proxyCountry != null) {
                        rows.push({ key: 'proxy', label: 'Proxy', value: [data.proxyType, data.proxyCountry].filter(Boolean).join(' / ') });
                      }

                      return (
                        <Table>
                          <Table.ScrollContainer>
                            <Table.Content aria-label='Summary'>
                              <Table.Header>
                                <Table.Column isRowHeader>Key</Table.Column>
                                <Table.Column>Value</Table.Column>
                              </Table.Header>
                              <Table.Body>
                                {rows.map((row) => (
                                  <Table.Row key={row.key}>
                                    <Table.Cell>{row.label}</Table.Cell>
                                    <Table.Cell>{row.value}</Table.Cell>
                                  </Table.Row>
                                ))}
                              </Table.Body>
                            </Table.Content>
                          </Table.ScrollContainer>
                        </Table>
                      );
                    })()}
                  </div>
                )}
                {tab === 'final' && (
                  <div className='overflow-y-auto p-4'>
                    <JsonView
                      value={finalMessage || null}
                      displayDataTypes={false}
                    />
                  </div>
                )}
                {tab === 'console' && (
                  <AceEditor
                    // mode="java"
                    theme='textmate'
                    readOnly={true}
                    width='100%'
                    height='100%'
                    value={consoleText}
                  />
                )}
                {tab === 'inputs' && (
                  <div className='overflow-y-auto p-4'>
                    <JsonView
                      value={query.data.find((e) => e.type === 'inputs')?.event ?? ''}
                      displayDataTypes={false}
                    />
                  </div>
                )}
                {tab === 'data' && (
                  <div className='h-full flex-col'>
                    <div className='p-3 bg-amber-100 text-xs'>
                      <Checkbox
                        className='text-xs'
                        isSelected={isShowLastData}
                        onChange={setShowLastData}
                      >
                        <Checkbox.Control>
                          <Checkbox.Indicator />
                        </Checkbox.Control>
                        <Checkbox.Content>
                          <Label>Show last data</Label>
                        </Checkbox.Content>
                      </Checkbox>
                    </div>
                    <AceEditor
                      mode='json'
                      theme='textmate'
                      readOnly={true}
                      width='100%'
                      height='100%'
                      value={JSON.stringify(
                        (isShowLastData || query.data?.length === playedEvents.length
                          ? finalMessage?.payload?.data
                          : lastDataItem) || '',
                        null,
                        2,
                      )}
                    />
                  </div>
                )}
                {tab === 'network' && <NetworkTable debuggerEvents={debuggerEvents} />}
                {tab === 'actions' && (
                  <div className='overflow-y-auto p-4'>
                    <JsonView
                      value={playedActions || []}
                      displayDataTypes={false}
                    />
                  </div>
                )}
                {tab === 'goto' && (
                  <div className='overflow-y-auto p-4'>
                    <JsonView
                      value={gotoOptions || []}
                      displayDataTypes={false}
                    />
                  </div>
                )}
                {tab === 'metrics' && (
                  <div className='overflow-y-auto p-4'>
                    <JsonView
                      value={metricsMessage || null}
                      displayDataTypes={false}
                    />
                  </div>
                )}
                {tab === 'tracer' && (
                  <div className='overflow-y-auto p-4'>
                    <JsonView
                      value={tracer || null}
                      displayDataTypes={false}
                    />
                  </div>
                )}
              </div>
            )}
          </Resizable>
        </div>
      </div>
    </div>
  );
}
